// react
import { ReactNode, useCallback, useContext, useEffect, useState } from 'react';

// context
import { ContextUser } from '../../../App.context';
import { ContextContracts, ContextProjects } from './Providers.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// types
import { Contract, Entity, Project } from '../../../@types';

// props
export interface ProvidersProps {
	children: ReactNode;
}

export const Providers = ({ children }: ProvidersProps) => {
	// session
	const token = sessionStorage.getItem('token');

	// context
	const { user } = useContext(ContextUser);

	// state
	const [contracts, setContracts] = useState<Contract[]>([]);
	const [isLoadingProjects, setIsLoadingProjects] = useState(false);
	const [projects, setProjects] = useState<Project[]>([]);

	// vars
	const entityIds = (user?.entities as Entity[])?.map((e) => e.id).join(',');
	const hasProjectV2 = projects.some((p) => p.version === '2');

	const {
		fetchRequest: fetchContracts,
		isLoading: isLoadingContracts,
		refetch: refetchContracts,
	} = useFetch({
		isLazy: true,
		onSuccess: (res) => {
			if (Array.isArray(res?.data)) {
				const contractsNormalized = res.data.map((row: Record<string, unknown>): Contract => {
					if ('contractNum' in row || 'dateSigned' in row) {
						return {
							...(row as object),
							id: (row.id as number) ?? (row.contractId as number),
							num: (row.contractNum as string) ?? '',
							dateSigning: (row.dateSigned as string) ?? undefined,
						} as Contract;
					}
					return row as unknown as Contract;
				});
				setContracts(
					// Supplier portal shows only ACTIVE (approved) contracts. v1 rows
					// carry no status, so keep those; drop DRAFT/SUBMITTED/etc. for v2.
					contractsNormalized.filter((c: Contract) => !c.status || c.status === 'ACTIVE')
				);
			}
		},
		options: {
			headers: {
				Authorization: `Bearer ${token}`,
				apikey: process.env.REACT_APP_APIKEY || '',
			},
		},
	});

	const fetchProjects = useCallback(() => {
		if (!token || !user) return;
		const userProjects = user.projects as Project[];
		if (!userProjects?.length) return;
		const headers = {
			Authorization: `Bearer ${token}`,
			apikey: process.env.REACT_APP_APIKEY || '',
		};
		setIsLoadingProjects(true);
		Promise.all(
			userProjects.map((p) => {
				const id = typeof p === 'number' ? p : p.id;
				return fetch(
					`${process.env.REACT_APP_API_URL}/api/proxies/projects/${id}?populate=country,program,program.hero,program.logo`,
					{ headers }
				)
					.then((res) => res.json())
					.then((res) => res.data?.[0] as Project | undefined);
			})
		)
			.then((results) => {
				setProjects(results.filter((p): p is Project => Boolean(p)));
			})
			.finally(() => {
				setIsLoadingProjects(false);
			});
	}, [token, user]);

	console.log('Projects in Providers:', projects);

	// fetch all user projects once token and user are present
	useEffect(() => {
		fetchProjects();
	}, [fetchProjects]);

	// fetch contracts once the project versions are known
	useEffect(() => {
		if (!token || !user || !projects.length || !entityIds) {
			return;
		}
		const headers = {
			Authorization: `Bearer ${token}`,
			apikey: process.env.REACT_APP_APIKEY || '',
		};
		const url = hasProjectV2
			? `${process.env.REACT_APP_API_URL}/api/proxiesV2/contracts?supplier=${entityIds}&limit=100`
			: `${process.env.REACT_APP_API_URL}/api/proxies/contracts?entities=${entityIds}`;
		fetchContracts(url, { headers });
	}, [fetchContracts, hasProjectV2, token, user, projects, entityIds]);

	return (
		<ContextProjects.Provider
			value={{
				hasProjectV2,
				isLoadingProjects,
				projects,
				refetchProjects: fetchProjects,
				setProjects,
			}}>
			<ContextContracts.Provider
				value={{
					contracts,
					isLoadingContracts,
					paginationContracts: null,
					refetchContracts,
					setContracts,
				}}>
				{children}
			</ContextContracts.Provider>
		</ContextProjects.Provider>
	);
};
